import isEmpty from 'lodash/isEmpty';
import CARD_RANGES from '@tcp/core/src/components/features/account/AddEditCreditCard/container/AddEditCreditCard.constants';
import {
  CARD_TYPES,
  DEFAULT_ADDRESS_ID,
  HEADER_OFFSET,
  ORDER_SUMMARY_WIDTH_RATIO,
  ORDER_SUMMARY_WRAPPER_CLASSNAME,
  SCROLL_ACCORDION_DELAY,
  SCROLL_ACCORDION_OPTIONS,
} from './SinglePageCheckout.constants';
import logger from '../../../../../utils/loggerInstance';
import { getViewportInfo, isClient } from '../../../../../utils';

const convertToUserAddressFormat = (address = {}) => {
  if (!isEmpty(address)) {
    const { addressLine1, addressLine2 } = address;
    const addressLine = [addressLine1, addressLine2];
    return { ...address, addressLine };
  }
  return address;
};

const convertBillingToUserAddress = (address = {}) => {
  if (!isEmpty(address)) {
    const {
      addressId,
      firstName,
      lastName,
      addressLine1,
      addressLine2,
      addressType,
      city,
      state,
      zipCode,
      country,
    } = address;

    const addressLine = [addressLine1, addressLine2];

    return {
      addressId,
      firstName,
      lastName,
      address1: addressLine1,
      address2: addressLine2,
      c_addressType: addressType,
      city,
      stateCode: state,
      postalCode: zipCode,
      countryCode: country,
      addressLine,
    };
  }
  return address;
};

const unionAddresses = (shippingAddress, userAddresses) => {
  const unionMap = {};
  shippingAddress.forEach((item) => {
    unionMap[item.addressId] = item;
  });

  userAddresses.forEach((item) => {
    unionMap[item.addressId] = item;
  });

  return Object.keys(unionMap)
    .map((addressId) => unionMap[addressId])
    .sort((a, b) => {
      if (a.addressId === DEFAULT_ADDRESS_ID || a.preferred === 'true') return -1;
      return a.addressId > b.addressId;
    });
};

const checkValidShipmentMethod = (selectedShipmentMethodId, shipmentMethods) =>
  shipmentMethods &&
  shipmentMethods.length &&
  shipmentMethods.findIndex((s) => s.id === selectedShipmentMethodId) >= 0;

const isShippingSectionFilled = (
  shippingFormInput = {},
  shipmentMethods = [],
  isLoginV2FlowEnabled
) => {
  const selectedShipmentMethodId = shippingFormInput?.shipmentMethods?.shippingMethodId;
  const isShipmentMethodValid = checkValidShipmentMethod(selectedShipmentMethodId, shipmentMethods);
  const { firstName, lastName, addressLine1, city, state, zipCode, phoneNumber } =
    shippingFormInput;
  if (isLoginV2FlowEnabled) {
    return (
      selectedShipmentMethodId &&
      isShipmentMethodValid &&
      firstName &&
      lastName &&
      addressLine1 &&
      city &&
      state &&
      zipCode &&
      phoneNumber
    );
  }
  return (
    selectedShipmentMethodId &&
    isShipmentMethodValid &&
    firstName &&
    lastName &&
    addressLine1 &&
    city &&
    state &&
    zipCode
  );
};

const isSameCardTypeSelected = (cardType, cardTypeRadio, isPaymentRedesignEnabled) => {
  if (isPaymentRedesignEnabled) {
    return true;
  }

  const creditCardTypes = Object.keys(CARD_RANGES.CREDIT_CARDS_BIN_RANGES).filter(
    (card) => card !== CARD_TYPES.PLCC_CARD
  );

  return (
    cardType === cardTypeRadio ||
    (cardTypeRadio === CARD_TYPES.CREDIT_CARD && creditCardTypes.includes(cardType))
  );
};

/**
 * Get scroll position and other options
 * for anchoring to accordion section
 *
 * @param {*} ref
 * @returns {*} scrollOptions
 */
const getAccordionScrollOptions = (ref, bossSwitchEnabled) => {
  const { isMobile, isTablet } = isClient() ? getViewportInfo() : {};
  const { offsetTop = 0, offsetParent = {} } = (ref && ref.current) || {};
  const { offsetTop: offsetParentTop = 0 } = offsetParent || {};
  let headerOffset = bossSwitchEnabled ? 140 : HEADER_OFFSET.MOBILE;
  if (!isMobile) {
    if (bossSwitchEnabled) {
      headerOffset = 140;
    } else {
      headerOffset = isTablet ? HEADER_OFFSET.TABLET : HEADER_OFFSET.DESKTOP;
    }
  }
  return {
    ...SCROLL_ACCORDION_OPTIONS,
    top: offsetTop + offsetParentTop - headerOffset,
    left: 0,
  };
};

/**
 * Scroll to the passed ref
 *
 * @param {*} ref
 * @param {string} id
 */
const scrollAccordionCardIntoView = (ref, id, bossSwitchEnabled) => {
  try {
    setTimeout(() => {
      window.scrollTo(getAccordionScrollOptions(ref, bossSwitchEnabled));
    }, SCROLL_ACCORDION_DELAY[id]);
  } catch (e) {
    logger.error(`scrollAccordionCardIntoView ${id}`, e);
  }
};

/**
 * Get style to be applied on order summary to make it sticky
 */
const getStyleForOrderSummary = ({ clientWidth, hasError, isCompleteElementVisible }) => {
  const style = {};
  try {
    if (isClient() && isCompleteElementVisible !== null) {
      const { clientWidth: spcSectionWrapperWidth } = document.getElementsByClassName(
        ORDER_SUMMARY_WRAPPER_CLASSNAME
      )[0];
      if (clientWidth && spcSectionWrapperWidth) {
        style.position = 'fixed';
        style.right = `${(clientWidth - spcSectionWrapperWidth) / 2}px`;
        style.width = `${ORDER_SUMMARY_WIDTH_RATIO * spcSectionWrapperWidth}px`;
        style.top = hasError ? '126px' : '116px';
      }
    }
  } catch (e) {
    logger.error('Error in getStyleForOrderSummary', e);
  }
  return style;
};

export {
  convertToUserAddressFormat,
  convertBillingToUserAddress,
  unionAddresses,
  isShippingSectionFilled,
  checkValidShipmentMethod,
  isSameCardTypeSelected,
  scrollAccordionCardIntoView,
  getStyleForOrderSummary,
};
